<template>
	<v-tab-item>
		<v-row no-gutters justify="space-between" align="center">
			<h3 class="text-h6 font-weight-bold">製品情報</h3>
			<v-btn
				color="secondary"
				v-if="matter && matter.ordered_products && matter.ordered_products.length"
				@click="showOrderedProducts = !showOrderedProducts"
			>
				<v-icon class="mr-1">mdi-swap-horizontal</v-icon>
				<span v-if="showOrderedProducts">見積もり製品情報を表示</span>
				<span v-else>注文製品情報を表示</span>
			</v-btn>
		</v-row>
		<template v-if="matter">
			<template v-if="matter.contract">
				<template v-if="showOrderedProducts">
					<h4 class="text-sm-body-1 text-h6 font-weight-bold color-secondary">注文製品情報</h4>
					<div>
						<v-row
							v-for="(ordered_product, i) in matter.ordered_products"
							:key="ordered_product.id"
							no-gutters
							class="px-1 pt-2 show-unit"
						>
							<v-col :class="$vuetify.breakpoint.mdAndUp ? 'pr-2' : ''" cols="12" md="6">
								<h4 class="text-body-2 font-weight-bold color-secondary">製品情報{{ i + 1 }}</h4>
								<ShowDetailUnit :columnName="'製造番号'" :value="ordered_product.product_number" />
								<ShowDetailUnit :columnName="'製品名'" :value="ordered_product.product.name" />
								<ShowDetailUnit :columnName="'サブネーム'" :value="ordered_product.sub_name" />
								<ShowDetailUnit
									:columnName="'単価'"
									:value="Number(ordered_product.unit_price).toLocaleString() + '円'"
								/>
								<ShowDetailUnit :columnName="'数量'" :value="ordered_product.quantity" />
								<ShowDetailUnit :columnName="'寸法'" :value="ordered_product.product.measurement" />
								<ShowDetailUnit
									v-if="ordered_product.product.aperture"
									:columnName="'口径(cm)'"
									:value="ordered_product.product.aperture"
								/>
								<ShowDetailUnit
									v-if="ordered_product.product.width"
									:columnName="'幅(cm)'"
									:value="ordered_product.product.width"
								/>
								<ShowDetailUnit
									v-if="ordered_product.product.length"
									:columnName="'長さ(cm)'"
									:value="ordered_product.product.length"
								/>
								<ShowDetailUnit
									v-if="ordered_product.product.height"
									:columnName="'高さ(cm)'"
									:value="ordered_product.product.height"
								/>
							</v-col>
							<v-col :class="$vuetify.breakpoint.mdAndUp ? 'pl-2' : ''" cols="12" md="6">
								<h4 class="text-body-2 font-weight-bold color-secondary">製造単位別製品情報</h4>
								<div>
									<div
										v-for="(info, i) in individualInfos(ordered_product)"
										:key="i"
										class="pt-2 top-divider"
									>
										<ShowDetailUnit :columnName="'枝番号'" :value="info.sub_number">
											<template v-slot:value>
												<v-row no-gutters justify="space-between">
													<p class="text-body-2" v-text="info.sub_number" />
													<v-btn
														small
														color="secondary"
														@click="$emit('fetch-order-individual-info', info.id)"
													>
														確認
													</v-btn>
												</v-row>
											</template>
										</ShowDetailUnit>
										<ShowDetailUnit :columnName="'個数'" :value="info.quantity" />
										<ShowDetailUnit :columnName="'状況'" :value="info.status_i18n" />
										<ShowDetailUnit :columnName="'施工場所'" :value="info.construction_place" />
										<ShowDetailUnit
											:columnName="'製造開始日'"
											:value="common.formatDateWithNullableTime(info, 'start_date', 'start_time')"
										/>
										<ShowDetailUnit
											:columnName="'製造終了日'"
											:value="common.formatDateWithNullableTime(info, 'end_date', 'end_time')"
										/>
									</div>
								</div>
							</v-col>
						</v-row>
					</div>
				</template>
				<template v-else>
					<h4 class="text-sm-body-1 text-h6 font-weight-bold color-secondary">見積もり製品情報</h4>
					<div>
						<v-row
							v-for="(matter_product, i) in matter.matter_products"
							:key="matter_product.id"
							no-gutters
							class="px-1 pt-2 show-unit"
						>
							<v-col :class="$vuetify.breakpoint.mdAndUp ? 'pr-2' : ''" cols="12" md="6">
								<h4 class="text-body-2 font-weight-bold color-secondary">製品情報{{ i + 1 }}</h4>
								<ShowDetailUnit :columnName="'製品名'" :value="matter_product.product.name" />
								<ShowDetailUnit :columnName="'サブネーム'" :value="matter_product.sub_name" />
								<ShowDetailUnit
									:columnName="'単価'"
									:value="Number(matter_product.unit_price).toLocaleString() + '円'"
								/>
								<ShowDetailUnit :columnName="'数量'" :value="matter_product.quantity" />
								<ShowDetailUnit :columnName="'寸法'" :value="matter_product.product.measurement" />
								<ShowDetailUnit
									v-if="matter_product.product.aperture"
									:columnName="'口径(cm)'"
									:value="matter_product.product.aperture"
								/>
								<ShowDetailUnit
									v-if="matter_product.product.width"
									:columnName="'幅(cm)'"
									:value="matter_product.product.width"
								/>
								<ShowDetailUnit
									v-if="matter_product.product.length"
									:columnName="'長さ(cm)'"
									:value="matter_product.product.length"
								/>
								<ShowDetailUnit
									v-if="matter_product.product.height"
									:columnName="'高さ(cm)'"
									:value="matter_product.product.height"
								/>
							</v-col>
							<v-col :class="$vuetify.breakpoint.mdAndUp ? 'pl-2' : ''" cols="12" md="6">
								<h4 class="text-body-2 font-weight-bold color-secondary">製造単位別製品情報</h4>
								<p
									v-if="matter.ordered_products && matter.ordered_products.length"
									class="text-body-2"
								>
									この案件は既は受注しているため、注文製品情報から内容をご確認ください。
								</p>
								<div v-else>
									<div
										v-for="(info, i) in individualInfos(matter_product)"
										:key="i"
										class="pt-2 top-divider"
									>
										<ShowDetailUnit :columnName="'枝番号'" :value="info.sub_number">
											<template v-slot:value>
												<v-row no-gutters justify="space-between">
													<p class="text-body-2" v-text="info.sub_number" />
													<v-btn
														small
														color="secondary"
														@click="$emit('fetch-order-individual-info', info.id)"
													>
														確認
													</v-btn>
												</v-row>
											</template>
										</ShowDetailUnit>
										<ShowDetailUnit :columnName="'個数'" :value="info.quantity" />
										<ShowDetailUnit :columnName="'状況'" :value="info.status_i18n" />
										<ShowDetailUnit :columnName="'施工場所'" :value="info.construction_place" />
										<ShowDetailUnit
											:columnName="'製造開始日'"
											:value="common.formatDateWithNullableTime(info, 'start_date', 'start_time')"
										/>
										<ShowDetailUnit
											:columnName="'製造終了日'"
											:value="common.formatDateWithNullableTime(info, 'end_date', 'end_time')"
										/>
									</div>
								</div>
							</v-col>
						</v-row>
					</div>
				</template>
			</template>

			<template v-else>
				<h4 class="text-sm-body-1 text-h6 font-weight-bold color-secondary">見積もり製品情報</h4>
				<div>
					<v-row
						v-for="(matter_product, i) in matter.matter_products"
						:key="matter_product.id"
						no-gutters
						class="px-1 pt-2 show-unit"
					>
						<v-col :class="$vuetify.breakpoint.mdAndUp ? 'pr-2' : ''" cols="12" md="6">
							<h4 class="text-body-2 font-weight-bold color-secondary">製品情報{{ i + 1 }}</h4>
							<ShowDetailUnit :columnName="'製品名'" :value="matter_product.product.name" />
							<ShowDetailUnit :columnName="'サブネーム'" :value="matter_product.sub_name" />
							<ShowDetailUnit
								:columnName="'単価'"
								:value="Number(matter_product.unit_price).toLocaleString() + '円'"
							/>
							<ShowDetailUnit :columnName="'数量'" :value="matter_product.quantity" />
							<ShowDetailUnit :columnName="'寸法'" :value="matter_product.product.measurement" />
							<ShowDetailUnit
								v-if="matter_product.product.aperture"
								:columnName="'口径(cm)'"
								:value="matter_product.product.aperture"
							/>
							<ShowDetailUnit
								v-if="matter_product.product.width"
								:columnName="'幅(cm)'"
								:value="matter_product.product.width"
							/>
							<ShowDetailUnit
								v-if="matter_product.product.length"
								:columnName="'長さ(cm)'"
								:value="matter_product.product.length"
							/>
							<ShowDetailUnit
								v-if="matter_product.product.height"
								:columnName="'高さ(cm)'"
								:value="matter_product.product.height"
							/>
						</v-col>
						<v-col :class="$vuetify.breakpoint.mdAndUp ? 'pl-2' : ''" cols="12" md="6">
							<h4 class="text-body-2 font-weight-bold color-secondary">製造単位別製品情報</h4>
							<div>
								<div
									v-for="(info, i) in individualInfos(matter_product)"
									:key="i"
									class="pt-2 top-divider"
								>
									<ShowDetailUnit :columnName="'枝番号'" :value="info.sub_number">
										<template v-slot:value>
											<v-row no-gutters justify="space-between">
												<p class="text-body-2" v-text="info.sub_number" />
												<v-btn
													small
													color="secondary"
													@click="$emit('fetch-order-individual-info', info.id)"
												>
													確認
												</v-btn>
											</v-row>
										</template>
									</ShowDetailUnit>
									<ShowDetailUnit :columnName="'個数'" :value="info.quantity" />
									<ShowDetailUnit :columnName="'状況'" :value="info.status_i18n" />
									<ShowDetailUnit :columnName="'施工場所'" :value="info.construction_place" />
									<ShowDetailUnit
										:columnName="'製造開始日'"
										:value="common.formatDateWithNullableTime(info, 'start_date', 'start_time')"
									/>
									<ShowDetailUnit
										:columnName="'製造終了日'"
										:value="common.formatDateWithNullableTime(info, 'end_date', 'end_time')"
									/>
								</div>
							</div>
						</v-col>
					</v-row>
				</div>
			</template>
		</template>
	</v-tab-item>
</template>

<script>
import axios from "axios"
import errorHandling from "../../../helper/errorHandling"
import authenticate from "../../../helper/authenticate"

import common from "../../../helper/common"
import ShowDetailUnit from "../../shared/ShowDetailUnit.vue"

export default {
	props: {
		id: {
			type: Number,
			required: false,
			default: null,
		},
		isFetched: {
			type: Boolean,
		},
	},
	components: {
		ShowDetailUnit,
	},
	data() {
		return { common, showOrderedProducts: true, matter: {} }
	},
	computed: {
		individualInfos() {
			return (product) => {
				if (!product.order_individual_infos) return []
				return product.order_individual_infos.filter((c) => !c.destroy_flag)
			}
		},
	},
	methods: {
		fetchProducts() {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/order_individual_infos/${this.id}/products`
			axios
				.get(url, {
					headers,
				})
				.then((res) => {
					this.matter = res.data.matter
					if (!this.matter.ordered_products?.length) this.showOrderedProducts = false
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
	},
	watch: {
		isFetched(newVal, oldVal) {
			if (oldVal == false && newVal == true) {
				this.fetchProducts()
			} else if (oldVal == true && newVal == false) {
			}
		},
		deep: true,
	},
}
</script>